/* ABAC policy bundles over the real API. No `X-Lumiq-Tenant` header — hard rule 1.
 *
 * **A policy is built from rules, and nothing here sends Rego.** The client posts a list of
 * `{role, action, resource_type, resource_id, effect}` and the platform validates every reference,
 * generates the module, compiles it against an entrypoint it chose, and evaluates it once before
 * storing. OPA is an implementation detail of the server rather than a vocabulary this feature has
 * to speak — the one place Rego appears is a read-only advanced view of what was compiled.
 *
 * **Still deliberately no upload control.** An artifact reaches this platform as compiled OPA WASM;
 * a file picker for arbitrary WASM is the exact affordance that makes somebody skip a review, and
 * the marketplace screen refuses one for the same reason. Building a policy is a different thing:
 * the bytes are ones this deployment produced from rules a person chose out of closed lists.
 *
 * **`/v1/policy/active` answers a different question from the others and that is the point.** The
 * bundle list is what this tenant has *stored*; `active` is what this *process* has actually
 * instantiated, keyed on the content hash of the bytes it is running. `ActiveBundleRegistry` is a lazy
 * cache by design — the registry is not transactional and the publish is — so a freshly activated
 * bundle reads as empty until something evaluates. §9 records that as reading like a failure when it
 * is not, which is why the screen renders both and explains the gap.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isAbacPosture,
  isActivation,
  isAuthoringResult,
  isBundleDeletion,
  isBundleVersion,
  isEnforcementState,
  isLoadedBundle,
  isPolicyBundle,
  isPolicyValidation,
  isPolicyVocabulary,
  isVersionPolicy,
} from '@/shared/api/generated/policy';
import type {
  AbacPosture,
  Activation,
  BundleDeletion,
  AuthoringResult,
  BundleVersion,
  EnforcementState,
  LoadedBundle,
  PolicyBundle,
  PolicyDocument,
  PolicyValidation,
  PolicyVocabulary,
  VersionPolicy,
} from '@/shared/api/generated/policy';
import {
  forgetIdempotencyKey,
  getOne,
  idempotencyKeyFor,
  post,
  postExpectingRefusal,
  put,
  removeOne,
} from '@/shared/api/http';

const BUNDLES_URL = '/v1/policy/bundles';
const ACTIVE_URL = '/v1/policy/active';
const ENFORCEMENT_URL = '/v1/policy/enforcement';
const VOCABULARY_URL = '/v1/policy/vocabulary';
const VALIDATE_URL = '/v1/policy/validate';
const ABAC_URL = '/v1/policy/abac';

/** Item guards. S-71: the generated envelope guard checks `Array.isArray` and stops. */
function isBundleList(
  value: unknown,
): value is { readonly items: readonly PolicyBundle[] } {
  if (typeof value !== 'object' || value === null) return false;
  const items = (value as Record<string, unknown>)['items'];
  return Array.isArray(items) && items.every(isPolicyBundle);
}

function isVersionList(
  value: unknown,
): value is { readonly items: readonly BundleVersion[] } {
  if (typeof value !== 'object' || value === null) return false;
  const items = (value as Record<string, unknown>)['items'];
  return Array.isArray(items) && items.every(isBundleVersion);
}

export interface ActiveView {
  readonly items: readonly LoadedBundle[];
  /** The wasmtime wheel is installed. **Says nothing about whether policy is enforcing.** */
  readonly runtimeAvailable: boolean;
  /** The evaluator is wired into authorization at all. The flag that was missing, and whose
   * absence let this screen report "Policy runtime available" on an inert deployment. */
  readonly abacEnabled: boolean;
  /** Which bundle name governs authorization. Only a bundle of this name is consulted. */
  readonly authzBundleName: string;
  /** Engine **and** evaluator **and** this tenant's bundle loaded. Derived server-side so a
   * client cannot assemble it from the wrong flag, which is exactly what happened. */
  readonly enforcementOperational: boolean;
  readonly maxStalenessSeconds: number;
}

function isActiveView(value: unknown): value is {
  readonly items: readonly LoadedBundle[];
  readonly meta: {
    readonly runtime_available: boolean;
    readonly abac_enabled: boolean;
    readonly authz_bundle_name: string;
    readonly enforcement_operational: boolean;
    readonly max_staleness_seconds: number;
  };
} {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  const items = candidate['items'];
  const meta = candidate['meta'];
  if (!Array.isArray(items) || !items.every(isLoadedBundle)) return false;
  if (typeof meta !== 'object' || meta === null) return false;
  const shape = meta as Record<string, unknown>;
  /* `runtime_available` is checked rather than defaulted, and that matters: §9 records the policy
   * router hard-coding it `false` while an engine was wired, so a screen that treated a missing field
   * as "no runtime" would report the same wrong thing from the other side. */
  return (
    typeof shape['runtime_available'] === 'boolean' &&
    typeof shape['abac_enabled'] === 'boolean' &&
    typeof shape['authz_bundle_name'] === 'string' &&
    typeof shape['enforcement_operational'] === 'boolean' &&
    typeof shape['max_staleness_seconds'] === 'number'
  );
}

export function usePolicyBundles(): UseQueryResult<readonly PolicyBundle[]> {
  return useQuery({
    queryKey: ['policy', 'bundles'],
    queryFn: async () =>
      (await getOne(BUNDLES_URL, isBundleList, 'policy bundles')).items,
  });
}

/** How one bundle's versions are read, as options rather than as a hook.
 *
 * **Two callers need the same read and one of them cannot use a hook.** The versions table reads the
 * selected bundle; the bundles table needs a version count and a running chip *per row*, and the number
 * of rows is data — so that side goes through `useQueries`, which takes options and not hooks. Sharing
 * this factory is what keeps the two on one cache key: declared separately they would fetch the same
 * URL under two keys, so selecting a bundle would refetch what the table had already read.
 */
export function versionsQuery(bundleId: string): {
  readonly queryKey: readonly unknown[];
  readonly queryFn: () => Promise<readonly BundleVersion[]>;
} {
  return {
    queryKey: ['policy', 'versions', bundleId],
    queryFn: async () =>
      (
        await getOne(
          `${BUNDLES_URL}/${bundleId}/versions`,
          isVersionList,
          'bundle versions',
        )
      ).items,
  };
}

export function useBundleVersions(
  bundleId: string | null,
): UseQueryResult<readonly BundleVersion[]> {
  return useQuery({
    queryKey: ['policy', 'versions', bundleId],
    enabled: bundleId !== null,
    /* Guarded rather than spread from `versionsQuery(bundleId ?? '')`. That spelling type-checks and
     * builds a closure over an empty id — unreachable while `enabled` is false, and a request to
     * `/bundles//versions` the day somebody removes the guard. */
    queryFn: async () => {
      if (bundleId === null) return [];
      return versionsQuery(bundleId).queryFn();
    },
  });
}

export function useActiveBundles(): UseQueryResult<ActiveView> {
  return useQuery({
    queryKey: ['policy', 'active'],
    queryFn: async () => {
      const view = await getOne(ACTIVE_URL, isActiveView, 'active policy bundles');
      return {
        items: view.items,
        runtimeAvailable: view.meta.runtime_available,
        abacEnabled: view.meta.abac_enabled,
        authzBundleName: view.meta.authz_bundle_name,
        enforcementOperational: view.meta.enforcement_operational,
        maxStalenessSeconds: view.meta.max_staleness_seconds,
      };
    },
    /* Short. This is a view of one process's memory, and the whole reason to render it is that it can
     * disagree with the stored state — a stale reading would hide exactly the disagreement. */
    staleTime: 10 * 1000,
  });
}

/** Why policy is or is not deciding anything for this tenant, and why not when it is not.
 *
 * **A second read rather than more fields on `/active`, because the two answer different
 * questions.** `/active` reports what the registry *holds*, so every failure renders there as an
 * empty list — and this screen previously explained that absence with a mechanism ("the registry
 * fills on demand") that could not fire, because nothing called `ensure_loaded`. A reader waited
 * for a state that would never arrive. This route attempts the load and names the outcome.
 *
 * Short `staleTime` for `useActiveBundles`' reason: it is a view of one process's live state, and
 * the whole point of rendering it is that it can change without this screen doing anything.
 */
export function useEnforcement(): UseQueryResult<EnforcementState> {
  return useQuery({
    queryKey: ['policy', 'enforcement'],
    queryFn: () =>
      getOne(ENFORCEMENT_URL, isEnforcementState, 'policy enforcement state'),
    staleTime: 10 * 1000,
    retry: false,
  });
}

/** One bundle's activation history, newest first.
 *
 * **The read the module was built around and never had.** `policy.activation_log` is append-only
 * by grant so an auditor can answer "what was permitted on 3 March"; nothing exposed it, so the
 * record was write-only. `enabled` on a selection, because there is no history to fetch until a
 * reader has chosen a bundle.
 */
export function useActivations(
  bundleId: string | null,
): UseQueryResult<readonly Activation[]> {
  return useQuery({
    queryKey: ['policy', 'activations', bundleId],
    enabled: bundleId !== null,
    queryFn: async () => {
      if (bundleId === null) return [];
      const page = await getOne(
        `${BUNDLES_URL}/${bundleId}/activations`,
        isActivationList,
        'policy activations',
      );
      return page.items;
    },
    retry: false,
  });
}

function isActivationList(
  value: unknown,
): value is { readonly items: readonly Activation[] } {
  if (typeof value !== 'object' || value === null) return false;
  const items = (value as Record<string, unknown>)['items'];
  return Array.isArray(items) && items.every(isActivation);
}

/** What a rule may be built from: this tenant's roles, the governed resources, the effects.
 *
 * **Not hardcoded here, and that is the whole reason the route exists.** A rule naming a role, an
 * action or a resource type that does not exist compiles, loads and matches nothing — so the policy
 * decides less than its author believes and reports nothing. A copy of these lists in TypeScript
 * would keep working while offering choices the platform no longer governs, or omitting a role the
 * tenant created yesterday.
 *
 * Not cached forever, unlike the contract it replaced: `roles` is a tenant's own rows, and somebody
 * who has just created a role should see it without reloading the app. A minute is long enough that
 * opening the builder twice costs one request.
 */
export function usePolicyVocabulary(): UseQueryResult<PolicyVocabulary> {
  return useQuery({
    queryKey: ['policy', 'vocabulary'],
    queryFn: () => getOne(VOCABULARY_URL, isPolicyVocabulary, 'policy vocabulary'),
    staleTime: 60_000,
  });
}

/** Checks a set of rules without storing them.
 *
 * A mutation rather than a query even though it changes nothing, because it is driven by a button
 * and must not run on mount, refetch on focus, or be served from a cache keyed on rules somebody is
 * still editing.
 */
export function useValidatePolicy(): UseMutationResult<
  PolicyValidation,
  Error,
  PolicyDocument
> {
  return useMutation({
    mutationFn: (policy: PolicyDocument) =>
      /* A fixed idempotency scope: the route creates nothing and is registered in
       * `NON_CREATING_POST_PATHS`, so the header is accepted and ignored. A key derived from the
       * rules would mint a new one per edit for a request that stores nothing. */
      post(
        VALIDATE_URL,
        idempotencyKeyFor('policy-validate', 'rules'),
        isPolicyValidation,
        'policy validation',
        policy,
      ),
  });
}

export interface AuthorArgs {
  readonly bundleId: string;
  readonly policy: PolicyDocument;
}

/** Compiles a structured policy and stores it as the draft. **A 422 is a result, not an error.**
 *
 * `postExpectingRefusal` keeps the per-rule problems: a refused policy answers 422 with row
 * numbers, and routing that through the ordinary error path would keep one message and discard
 * every attribution. Callers branch on `stored`, never on whether `problems` is empty — a client
 * inferring success from an empty list would report a failed request as a saved draft.
 *
 * The idempotency key is forgotten only on a *stored* draft. A refusal is not a completed request,
 * so a retry after fixing a rule must be a new one rather than a replay of the failure.
 */
export function useAuthorFromRules(): UseMutationResult<
  AuthoringResult,
  Error,
  AuthorArgs
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ bundleId, policy }: AuthorArgs) =>
      postExpectingRefusal(
        `${BUNDLES_URL}/${bundleId}/versions/from-policy`,
        idempotencyKeyFor('policy-author', `${bundleId}:${JSON.stringify(policy)}`),
        isAuthoringResult,
        'policy authoring',
        policy,
        422,
      ),
    onSuccess: (result, { bundleId, policy }) => {
      if (!result.stored) return;
      forgetIdempotencyKey('policy-author', `${bundleId}:${JSON.stringify(policy)}`);
      void client.invalidateQueries({ queryKey: ['policy', 'versions', bundleId] });
      /* The draft's rules too: they are exactly what this replaced. */
      void client.invalidateQueries({ queryKey: ['policy', 'rules', bundleId, 'draft'] });
    },
  });
}

/** The rules a version was built from, so the builder can reopen exactly what was saved.
 *
 * `rules: null` with `authored_here: false` is an ordinary answer — the version was uploaded as a
 * pre-compiled artifact — rather than a failure, which is why this is a 200 and not a 404. It also
 * carries the generated Rego for the read-only advanced view; nothing in the normal workflow needs
 * that.
 */
export function useVersionPolicy(
  bundleId: string | null,
  semver: string | null,
): UseQueryResult<VersionPolicy> {
  return useQuery({
    ...versionPolicyQuery(bundleId ?? '', semver ?? ''),
    /* Both halves, because a null either side would build `/versions//policy` and ask the server
     * about a version named by nothing. */
    enabled: bundleId !== null && semver !== null,
  });
}

/** One version's rules as query options, so a per-version list (`useQueries`) and a single read
 * share a cache key — declared separately, opening a version would refetch what the list read. */
export function versionPolicyQuery(
  bundleId: string,
  semver: string,
): {
  readonly queryKey: readonly unknown[];
  readonly queryFn: () => Promise<VersionPolicy>;
} {
  return {
    queryKey: ['policy', 'rules', bundleId, semver],
    queryFn: () =>
      getOne(
        `${BUNDLES_URL}/${bundleId}/versions/${semver}/policy`,
        isVersionPolicy,
        'policy version rules',
      ),
  };
}

/** The rules the bundle's draft was built from.
 *
 * **What makes "save today, publish tomorrow" work.** A draft has no semver, so the versioned read
 * cannot reach it; without this the editor could only reopen the *active* version's rules, and a
 * saved draft would be silently rebuilt over the next time somebody opened the screen.
 *
 * `enabled` on the caller's knowledge that a draft exists — the version list says so — because a
 * bundle with no draft answers 404, which is the normal state between publishes rather than a fault.
 */
export function useDraftPolicy(
  bundleId: string | null,
  hasDraft: boolean,
): UseQueryResult<VersionPolicy> {
  return useQuery({
    ...draftPolicyQuery(bundleId ?? ''),
    enabled: bundleId !== null && hasDraft,
    retry: false,
  });
}

export function draftPolicyQuery(bundleId: string): {
  readonly queryKey: readonly unknown[];
  readonly queryFn: () => Promise<VersionPolicy>;
} {
  return {
    queryKey: ['policy', 'rules', bundleId, 'draft'],
    queryFn: () =>
      getOne(
        `${BUNDLES_URL}/${bundleId}/draft/policy`,
        isVersionPolicy,
        'policy draft rules',
      ),
  };
}

function isAccepted(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object';
}

/** How the next semver is minted. The API's three values, not a superset. */
export type BumpKind = 'major' | 'minor' | 'patch';

export interface PublishRequest {
  readonly bundleId: string;
  readonly bump: BumpKind;
}

/** Publishes the draft **and makes it active** -- one call, which is the API's own shape.
 *
 * **`bump` is required and was never sent**, so this returned 422 for every bundle from the day
 * the screen was written: the body was `{}` and `PublishRequest` declares `required: [bump]`.
 *
 * It is the caller's declaration rather than a default here, because the contract says so in its
 * own words: deciding whether a changed policy is breaking needs an opinion about semantics that
 * nothing in the platform has, and a confidently wrong major number is something downstream pins
 * to. So the screen asks.
 */
export function usePublishBundle(): UseMutationResult<
  BundleVersion,
  Error,
  PublishRequest
> {
  const client = useQueryClient();
  return useMutation({
    /* Returns the version the server minted, so the confirmation names *that* number rather than
     * the one this screen predicted — the two agree, and only one of them is a fact. */
    mutationFn: ({ bundleId, bump }: PublishRequest) =>
      post(
        `${BUNDLES_URL}/${bundleId}/publish`,
        idempotencyKeyFor('policy-publish', bundleId),
        isBundleVersion,
        'policy publish',
        { bump },
      ),
    onSuccess: (_result, { bundleId }) => {
      forgetIdempotencyKey('policy-publish', bundleId);
      void client.invalidateQueries({ queryKey: ['policy', 'versions', bundleId] });
      /* The draft became a version, so its rules now live under a semver. */
      void client.invalidateQueries({ queryKey: ['policy', 'rules', bundleId, 'draft'] });
      /* And the active view, because publishing makes a version active — but see the screen: the
       * registry fills lazily, so `active` may still be empty immediately afterwards. Invalidating is
       * still right; pretending the two agree is what would be wrong. */
      void client.invalidateQueries({ queryKey: ['policy', 'active'] });
      void client.invalidateQueries({ queryKey: ['policy', 'enforcement'] });
      void client.invalidateQueries({ queryKey: ['policy', 'activations', bundleId] });
    },
  });
}

export interface ActivateArgs {
  readonly bundleId: string;
  readonly semver: string;
}

export function useActivateVersion(): UseMutationResult<void, Error, ActivateArgs> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ bundleId, semver }: ActivateArgs) => {
      await post(
        `${BUNDLES_URL}/${bundleId}/versions/${semver}/activate`,
        idempotencyKeyFor('policy-activate', `${bundleId}:${semver}`),
        isAccepted,
        'policy activate',
        {},
      );
    },
    onSuccess: (_result, { bundleId, semver }) => {
      forgetIdempotencyKey('policy-activate', `${bundleId}:${semver}`);
      void client.invalidateQueries({ queryKey: ['policy', 'versions', bundleId] });
      void client.invalidateQueries({ queryKey: ['policy', 'active'] });
      void client.invalidateQueries({ queryKey: ['policy', 'enforcement'] });
      void client.invalidateQueries({ queryKey: ['policy', 'activations', bundleId] });
    },
  });
}

/** Withdraw a version. **Destructive in a way the copy has to say.**
 *
 * Deprecating the only active version leaves the tenant with no active policy, and the platform
 * then **denies every request** rather than falling back to roles — deliberately, so that
 * deprecation cannot be used to switch scoping off from inside the product. The row's hint says
 * so; a control this consequential must not be discoverable only by pressing it.
 *
 * It also exists because without it `reactivate` was unreachable: nothing in this UI could produce
 * a deprecated version, so that branch of `actionFor` was dead.
 */
export function useDeprecateVersion(): UseMutationResult<void, Error, ActivateArgs> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ bundleId, semver }: ActivateArgs) => {
      await post(
        `${BUNDLES_URL}/${bundleId}/versions/${semver}/deprecate`,
        idempotencyKeyFor('policy-deprecate', `${bundleId}:${semver}`),
        isAccepted,
        'policy deprecate',
        {},
      );
    },
    onSuccess: (_result, { bundleId, semver }) => {
      forgetIdempotencyKey('policy-deprecate', `${bundleId}:${semver}`);
      void client.invalidateQueries({ queryKey: ['policy', 'versions', bundleId] });
      void client.invalidateQueries({ queryKey: ['policy', 'active'] });
      void client.invalidateQueries({ queryKey: ['policy', 'enforcement'] });
      void client.invalidateQueries({ queryKey: ['policy', 'activations', bundleId] });
    },
  });
}

/* ---------------------------------------------------------------- the enforcement setting

 * Whether this tenant enforces attribute policy at all.
 *
 * **The control that did not exist, and its absence made this screen a one-way door.** Enforcement
 * was `LUMIQ_ABAC_ENABLED` — a process-level variable — so within a tenant the only way to stop a
 * policy deciding was to deprecate its last active version, which the platform deliberately fails
 * *closed*. A tenant's only off switch denied everything.
 *
 * Read separately from `/enforcement` even though that route carries the same posture, because the
 * two are wanted at different times: this is what a toggle reads and writes, and the enforcement
 * banner is what a reader looks at. Both invalidate each other on a write, so they cannot disagree.
 */
export function useAbacPosture(): UseQueryResult<AbacPosture> {
  return useQuery({
    queryKey: ['policy', 'abac'],
    queryFn: () => getOne(ABAC_URL, isAbacPosture, 'ABAC enforcement setting'),
    /* Short, for `useEnforcement`'s reason: this is live state another administrator can change,
     * and the whole point of rendering it is that it moves without this screen doing anything. */
    staleTime: 10 * 1000,
    retry: false,
  });
}

/** Turn attribute policy on or off for this tenant. **Not a deprecation and not a deletion.**
 *
 * A PUT, so no `Idempotency-Key`: the resource is one row named by the tenant in the request's own
 * validated claim, and a replay writes the same value.
 *
 * **Off cannot widen a role grant**, which is why this is an ordinary control rather than a
 * two-eyes transition: roles are consulted first and a policy's only power is to narrow, so
 * declining to narrow leaves a decision a role already made.
 *
 * Invalidates the enforcement read as well as its own, because the banner's whole subject is what
 * this changed — leaving it stale would let the screen report "RBAC + Attribute Policy" for up to
 * its `staleTime` after somebody switched scoping off.
 */
export function useSetAbacEnabled(): UseMutationResult<AbacPosture, Error, boolean> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      put(ABAC_URL, isAbacPosture, 'ABAC enforcement setting', { enabled }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['policy', 'abac'] });
      void client.invalidateQueries({ queryKey: ['policy', 'enforcement'] });
      /* And the active view: switching scoping on or off changes whether what this process holds is
       * consulted, which is exactly what that panel is about. */
      void client.invalidateQueries({ queryKey: ['policy', 'active'] });
    },
  });
}

/* ---------------------------------------------------------------- deleting a bundle

 * Withdraw a bundle identity. **Refused with a 409 while one of its versions is active.**
 *
 * Every bundle name is deletable — `authz`, `customers`, anything a tenant created — because the
 * alternative is a screen on which an identity created by mistake can never be removed, and for the
 * governing bundle that made adopting attribute policy a one-way door.
 *
 * **Deleting is not disabling**, and the response says so rather than leaving it to this client:
 * `abac_unchanged` is always true and `note` is host-written, so a screen that rendered nothing else
 * still could not claim the opposite. The confirmation copy states it too, because it is the one
 * thing about this operation a reader is most likely to get wrong.
 *
 * `removeOne` rather than `remove`: the body carries the bundle that was deleted, so the success
 * message names it from the response instead of from what the caller believed it was deleting.
 */
export function useDeleteBundle(): UseMutationResult<BundleDeletion, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (bundleId: string) =>
      removeOne(`${BUNDLES_URL}/${bundleId}`, isBundleDeletion, 'policy bundle deletion'),
    onSuccess: (_result, bundleId) => {
      void client.invalidateQueries({ queryKey: ['policy', 'bundles'] });
      void client.invalidateQueries({ queryKey: ['policy', 'versions', bundleId] });
      void client.invalidateQueries({ queryKey: ['policy', 'active'] });
      void client.invalidateQueries({ queryKey: ['policy', 'enforcement'] });
      /* Deliberately **not** the ABAC setting. Deleting a bundle changes it not at all, and
       * refetching it here would suggest to the next reader of this file that it might. */
    },
  });
}

export interface NewBundle {
  readonly name: string;
  readonly description: string;
}

/** Register a bundle *identity* — a name and one line on what it decides. No bytes.
 *
 * **The one mutation on this screen that is not a lifecycle step, and it is deliberately harmless.**
 * `POST /v1/policy/bundles` creates a row with no versions, so the bundle decides nothing until
 * compiled WASM arrives through the reviewed pipeline. That separation is the whole reason there is
 * no file picker anywhere near it: creating a name and uploading bytes that will run are different
 * acts, and only the second one needs a review.
 *
 * The name is unique per tenant and the API answers 409 when it is taken, because the evaluator's
 * cache is keyed on it — two bundles sharing a name would make "the active authz bundle" ambiguous
 * in the one place where ambiguity is a silent privilege change. The refusal is shown verbatim.
 *
 * Keyed on the name rather than on a constant, so a retried submission of the same bundle carries the
 * same key while a genuinely different bundle gets a new one. A constant would make the second
 * creation a replay of the first.
 */
export function useCreateBundle(): UseMutationResult<PolicyBundle, Error, NewBundle> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (bundle: NewBundle) =>
      post(
        BUNDLES_URL,
        idempotencyKeyFor('policy-bundle', bundle.name),
        isPolicyBundle,
        'policy bundle',
        { name: bundle.name, description: bundle.description },
      ),
    onSuccess: (_result, bundle) => {
      forgetIdempotencyKey('policy-bundle', bundle.name);
      void client.invalidateQueries({ queryKey: ['policy', 'bundles'] });
      /* Not the active view: a new identity has no versions, so nothing about what this process is
       * running can have changed. Invalidating it would refetch to learn the same answer. */
    },
  });
}
